<template>
  <v-dialog v-model="dialog" :persistent="!$store.getters.impersonatedUserId" width="90vw" max-width="1000px">
    <v-card color="#EEF2F5" class="postponement-card text-center" :class="isSmallScreen ? 'smallScreen py-2' : 'pa-8'" rounded="lg">
      <v-card-title>
        <h2 :class="isSmallScreen ? 'text-h6' : 'text-h5'">🕒 {{ $t('PostponementRequestDialog.title') }}</h2>
      </v-card-title>
      <v-card-text :class="isSmallScreen ? 'px-0 pb-2 pt-4' : ''">
        <div :class="isSmallScreen ? 'text-subtitle-1' : 'text-h6 mt-4'" class="font-weight-light">
          {{ $t('PostponementRequestDialog.body') }}
        </div>

        <v-carousel
          v-model="carouselModel"
          :continuous="false"
          class="postponement-carousel"
          hide-delimiter-background
          height="400px"
          ref="carousel"
        >
          <v-carousel-item
            v-for="(postponement) in postponements"
            :key="postponement.deviceId"
            :value="postponement.deviceId"
          >
            <v-form :ref="`postponement-form-${postponement.deviceId}`">
              <v-row
                class="d-flex flex-column"
                :class="isSmallScreen ? 'px-6' : 'px-14'"
                align="center"
              >
                <div :class="isSmallScreen ? 'text-subtitle-1 pt-4' : 'text-h6 pt-10'">
                  {{ postponement.name }}
                </div>

                <div :class="isSmallScreen ? 'text-body-2' : 'text-subtitle-1'" class="pt-4" style="max-width: 600px;">
                  <strong>{{ $t('PostponementRequestDialog.changeReason') }}</strong>
                  {{ $t(`PostponementRequestDialog.changeType.${postponement.type}`) }}
                </div>

                <div class="pt-8" :style="isSmallScreen ? 'width: 200px;' : 'width: 300px;'">
                  <v-menu
                    close-on-content-click
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :key="`selected-date-${postponement.deviceId}`"
                        v-model="postponement.selectedDate"
                        :label="$t('PostponementRequestDialog.newStartDate')"
                        :title="$t('PostponementRequestDialog.newStartDate')"
                        background-color="white"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        persistent-hint
                        :hint="postponement.serviceStartDate ? `${$t('PostponementRequestDialog.currentStartDate')} ${postponement.serviceStartDate}` : ' '"
                        flat
                        color="#0098E1"
                        clearable
                        :rules="[rules.fieldRequired, rules.validateStartDate(postponement.serviceStartDate)]"
                        hide-details="auto"
                        append-icon="mdi-calendar"
                      />
                    </template>
                    <v-date-picker
                      v-model="postponement.auxSelectedDate"
                      @change="handleDateChange($event, postponement)"
                      color="#0098E1"
                      locale="es-AR"
                      :min="jsDateToISO(new Date())"
                    />
                  </v-menu>
                </div>

                <v-btn
                  color="#0098E1"
                  dark
                  :loading="postponement.loading"
                  @click="acceptPostponementHandler(postponement)"
                >
                  {{ $t('PostponementRequestDialog.confirm') }}
                </v-btn>
              </v-row>
            </v-form>
          </v-carousel-item>
        </v-carousel>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { CAMPAIGN_VALIDATION, EVENTS_TO_TRACK } from '../../constants/const'
import { acceptDevicePostponement } from '../../lib/crudOperations/write'
import { trackEvent } from '../../lib/track-events'

export default {
  props: {
    devices: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      carouselModel: 0,
      rules: {
        fieldRequired: v => !!v || this.$t('Config.fieldIsRequired'),
        validateStartDate: (currentStartDate) => (newStartDate) => {
          if (!newStartDate || !currentStartDate) return true // If no date, don't validate here (already validated with fieldRequired)
          const [currentDay, currentMonth, currentYear] = currentStartDate.split('/')
          const [day, month, year] = newStartDate.split('/')
          const selectedDate = new Date(year, month - 1, day)
          const parsedCurrentStartDate = new Date(currentYear, currentMonth - 1, currentDay)
          const minDate = new Date()
          minDate.setDate(minDate.getDate() + CAMPAIGN_VALIDATION.MIN_DAYS_BEFORE_START)
          return (selectedDate >= parsedCurrentStartDate || selectedDate >= minDate) || this.$t('Config.startDateMinDaysRule', {minDays: CAMPAIGN_VALIDATION.MIN_DAYS_BEFORE_START})
        },
      },
      postponements: []
    }
  },
  computed: {
    dialog: {
      get () {
        return this.postponements.length > 0
      }
    },
    isSmallScreen() {
      return this.$vuetify.breakpoint.xsOnly
    }
  },
  created () {
    this.initializePostponements(this.devices)
  },
  methods: {
    initializePostponements(devices) {
      this.postponements = devices.map(device => ({
        name: device.campaign.entityName,
        deviceId: device.id,
        serviceStartDate: device.serviceStartDate && this.jsDateToDDMMYYYY(device.serviceStartDate),

        ...device.postponementRequest,
        selectedDate: this.jsDateToDDMMYYYY(device.postponementRequest.suggestedDate),
        auxSelectedDate: this.jsDateToISO(device.postponementRequest.suggestedDate),
        loading: false
      }))
    },
    async acceptPostponementHandler (postponement) {
      try {
        trackEvent(EVENTS_TO_TRACK.CLICK_ACCEPT_POSTPONEMENT, {
          deviceId: postponement.deviceId,
          selectedDate: postponement.selectedDate
        })
        postponement.loading = true
        if (!this.$refs[`postponement-form-${postponement.deviceId}`][0].validate()) {
          postponement.loading = false
          return
        }

        await acceptDevicePostponement(postponement.deviceId, this.ddMMyyyyToJsDate(postponement.selectedDate))
        this.removePostponement(postponement)
      } catch (err) {
        console.error('ERROR_ACCEPT_POSTPONEMENT:', err)
        this.$root.snack.show({text: this.$t('UI.registerError'), error: true})
      } finally {
        postponement.loading = false
      }
    },
    removePostponement (postponement) {
      if (this.postponements.length > 0) {
        const indexes = this.postponements.map(p => p.deviceId)
        const carouselIndex = indexes.indexOf(postponement.deviceId)

        if (carouselIndex >= indexes.length - 1) {
          this.carouselModel = indexes[carouselIndex - 1] // If the last item is selected, go to the previous one
        } else {
          this.carouselModel = indexes[carouselIndex + 1] // Else, go to the next one
        }
      }

      setTimeout(() => { // Use setTimeout to give time to the carousel to update the index
        const updatedPostponements = this.postponements.filter(p => p.deviceId !== postponement.deviceId)
        this.postponements = updatedPostponements
      }, 100)

      // this.$emit('update:devices', updatedPostponements)
    },
    handleDateChange (val, postponement) {
      const [year, month, day] = val.split('-')
      const formattedDate = `${day}/${month}/${year}`
      postponement.selectedDate = formattedDate
      postponement.auxSelectedDate = val
    },
    jsDateToISO (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    jsDateToDDMMYYYY (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${day}/${month}/${year}`
    },
    ddMMyyyyToJsDate (date) {
      const [day, month, year] = date.split('/')
      return new Date(year, month - 1, day)
    }
  }
}
</script>



// WEBPACK FOOTER //
// PostponementRequestDialog.vue?3cdf9592